import { useState, useEffect, useRef, useMemo } from 'react'
import './EmojiPicker.css'

const WORKSPACE_EMOJIS = [
    // Work & productivity
    '🏠', '📁', '📂', '📋', '📝', '📌', '💼', '🗂️', '📊', '📈',
    // Tech & dev
    '💻', '⚙️', '🔧', '🛠️', '🧪', '🔬', '🤖', '🧠', '🚀', '⚡',
    // Creative
    '🎨', '✏️', '🖌️', '📐', '🎭', '🎵', '📸', '🎬', '🖊️', '📖',
    // Nature & symbols
    '🌱', '🌿', '🌸', '🌊', '☀️', '⭐', '🌙', '🔥', '💎', '🌈',
    // Objects & concepts
    '🔗', '🔑', '🏷️', '💡', '🎯', '🏆', '🛡️', '📦', '🧩', '🗃️',
    // Animals
    '🦊', '🐙', '🦋', '🐝', '🐢', '🦅', '🐬', '🦉', '🐧', '🐳',
    // People & fun
    '👾', '🎮', '🕹️', '🎲', '✨', '💫', '🌟', '🔮', '🎪', '🎠',
]

interface EmojiPickerProps {
    currentEmoji: string
    onSelect: (emoji: string) => void
    onClose: () => void
}

export default function EmojiPicker({ currentEmoji, onSelect, onClose }: EmojiPickerProps) {
    const [filter, setFilter] = useState('')
    const ref = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Close on outside click
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose()
            }
        }
        // Delay to avoid capturing the click that opened this
        const timer = setTimeout(() => window.addEventListener('mousedown', handleClick), 0)
        return () => { clearTimeout(timer); window.removeEventListener('mousedown', handleClick) }
    }, [onClose])

    useEffect(() => {
        inputRef.current?.focus()
    }, [])

    const filtered = useMemo(() => {
        if (!filter) return WORKSPACE_EMOJIS
        return WORKSPACE_EMOJIS.filter(e => e.includes(filter))
    }, [filter])

    return (
        <>
            {/* Backdrop scrim — only visible on mobile via CSS media query */}
            <div className="emoji-picker-backdrop" onClick={onClose} />
            <div className="emoji-picker" ref={ref}>
                <input
                    ref={inputRef}
                    className="emoji-picker-search"
                    placeholder="Search emoji…"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Escape') onClose()
                    }}
                />
                <div className="emoji-picker-grid">
                    {filtered.map(emoji => (
                        <button
                            key={emoji}
                            className={`emoji-picker-item ${emoji === currentEmoji ? 'active' : ''}`}
                            onClick={() => { onSelect(emoji); onClose() }}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
                {filtered.length === 0 && (
                    <div className="emoji-picker-empty">No matches</div>
                )}
            </div>
        </>
    )
}
